import { cn, Input } from "@gotts.ai/ui";
import { useEffect, useRef, useState } from "react";

import { capitalizeParamName, validateInput } from "./utils";

import type { KnownAddress } from "./useKnownAddresses";

interface FunctionInputProps {
  name: string;
  type: string;
  value: string;
  onChange: (value: string) => void;
  knownAddresses: KnownAddress[];
}

export function FunctionInput({
  name,
  type: solidityType,
  value,
  onChange,
  knownAddresses,
}: FunctionInputProps) {
  const [showAutocomplete, setShowAutocomplete] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const validation = validateInput(value, solidityType);
  const isAddress = solidityType === "address";

  // Filter autocomplete matches
  const matches =
    isAddress && value.length > 0
      ? knownAddresses.filter(
          (a) =>
            a.address.toLowerCase().includes(value.toLowerCase()) ||
            a.label.toLowerCase().includes(value.toLowerCase()),
        )
      : [];

  // Close autocomplete on click outside
  useEffect(() => {
    if (!showAutocomplete) return;
    function handleClick(e: MouseEvent) {
      if (
        containerRef.current &&
        !containerRef.current.contains(e.target as Node)
      ) {
        setShowAutocomplete(false);
      }
    }
    document.addEventListener("mousedown", handleClick);
    return () => {
      document.removeEventListener("mousedown", handleClick);
    };
  }, [showAutocomplete]);

  const borderClass = !value
    ? ""
    : validation.valid
      ? "input-valid"
      : "input-invalid";

  return (
    <div ref={containerRef} className="relative flex items-center gap-2">
      <label className="flex w-28 shrink-0 items-baseline gap-1">
        <span className="text-xs font-normal text-[var(--color-text)]">
          {capitalizeParamName(name)}
        </span>
        <span className="text-[10px] text-[var(--color-text-muted)]">
          ({solidityType})
        </span>
      </label>
      <div className="relative flex-1">
        <Input
          type="text"
          placeholder={solidityType}
          value={value}
          onChange={(e) => {
            onChange(e.target.value);
            if (isAddress) setShowAutocomplete(true);
          }}
          onFocus={() => {
            if (isAddress && matches.length > 0) setShowAutocomplete(true);
          }}
          className={cn("flex-1 font-mono text-xs", borderClass)}
        />
        {!validation.valid && validation.error && (
          <p className="mt-0.5 text-[9px] text-[var(--color-error)]">
            {validation.error}
          </p>
        )}

        {/* Address autocomplete */}
        {isAddress && showAutocomplete && matches.length > 0 && (
          <div className="absolute left-0 right-0 top-full z-50 mt-1 max-h-[200px] overflow-auto border border-[rgba(201,168,76,0.18)] bg-[rgba(16,16,14,0.95)] shadow-[4px_4px_0_rgba(0,0,0,0.40)]">
            {matches.slice(0, 8).map((addr) => (
              <button
                key={addr.address}
                type="button"
                onClick={() => {
                  onChange(addr.address);
                  setShowAutocomplete(false);
                }}
                className="flex w-full items-center gap-2 px-2.5 py-1.5 text-left hover:bg-[rgba(201,168,76,0.03)]"
              >
                <span className="text-xs text-[var(--color-text)]">
                  {addr.label}
                </span>
                <span className="ml-auto font-mono text-[10px] text-[var(--color-text-muted)]">
                  {addr.address.slice(0, 6)}...{addr.address.slice(-4)}
                </span>
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
